import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { Suspense, useMemo, useCallback, useEffect } from "react";
import clsx from "clsx";
import { useResource, useFetcher, useInvalidator } from "@rest-hooks/core";
import { makeStyles } from "@material-ui/core/styles";
import { ProofOfDeliverySmsActions } from "./ProofOfDeliverySmsActions";
import { LinearProgress, TablePagination } from "@material-ui/core";
import { ExpandableTable, ExpandedRow, Cells } from "@ploy-ui/core";
import { ApplicationStatus, DocumentStatus } from "@ploy-lib/types";
import { ProofOfDeliveryInformationResource, ProductType, ApplicationInfoResource, ProductResource, useResourceWithInvalidate } from "@ploy-lib/rest-resources";
const useStyles = makeStyles(theme => ({
    root: {
        width: "100%",
        flexGrow: 1,
        padding: theme.spacing(2),
        [theme.breakpoints.up("lg")]: {
            padding: theme.spacing(4)
        }
    },
    applicationStatusNameExternal: {
        width: 160,
        textAlign: "center"
    },
    applicationNumber: {
        width: 100
    },
    customerNameInner: {
        display: "flex",
        alignItems: "center",
        "& > div": {
            marginRight: 10
        }
    },
    customerNameText: {
        fontWeight: "bold"
    }
}), { name: "ProofOfDeliverySms" });
const defaultColumnData = [
    {
        id: "applicationStatusNameExternal",
        label: "Status",
        component: props => (_jsx(Cells.ColoredStatus, { statusKey: "applicationStatusId", ...props })),
        hidden: { xsDown: true },
        hiddenInExpandable: { smUp: true }
    },
    {
        id: "documentStatusNameExternal",
        label: "Dokumentstatus"
    },
    {
        id: "applicationNumber",
        label: "Saksnr.",
        hidden: { smDown: true }
    },
    {
        id: "customerName",
        label: "Kunde",
        component: props => (_jsx(Cells.DoubleLineSmDown, { statusKey: "applicationStatusId", secondLineKey: "applicationNumber", ...props }))
    },
    {
        id: "field5",
        label: "Merke"
    },
    {
        id: "field8",
        label: "Regnr."
    },
    {
        id: "productName",
        label: "Produkt",
        hidden: { smDown: true },
        hiddenInExpandable: { mdUp: true }
    },
    {
        id: "salesPersonName",
        label: "Selger",
        hidden: true
    },
    {
        component: ProofOfDeliverySmsActions
    }
];
const proofOfDeliveryFilter = {
    appStatus: [
        ApplicationStatus.AutoApproved,
        ApplicationStatus.ManuallyApproved
    ],
    fromDocStatus: DocumentStatus.CarOrderReceived,
    toDocStatus: DocumentStatus.ProofOfDeliveryAccepted,
    hasCarRegNo: true
};
export const PendingProofOfDelivery = ({ className, columnData = defaultColumnData }) => {
    const classes = useStyles();
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(20);
    const columns = useMemo(() => columnData.map(c => ({
        ...c,
        className: c.id == null
            ? undefined
            : classes[c.id]
    })), [columnData, classes]);
    const products = useResource(ProductResource.list(), {});
    const params = useMemo(() => {
        const productIds = products
            .filter(p => p.type === ProductType.Leasing)
            .map(p => p.id);
        return {
            productList: productIds,
            ...proofOfDeliveryFilter,
            pageSize: rowsPerPage,
            page: page + 1,
            sortBy: "createdDate.desc"
        };
    }, [page, products, rowsPerPage]);
    const { records, pageSize, recordCount } = useResource(ApplicationInfoResource.list(), params);
    const fetchApplications = useFetcher(ApplicationInfoResource.list());
    const reload = useCallback(() => fetchApplications(params), [fetchApplications, params]);
    return (_jsxs("div", { className: clsx(className, classes.root), children: [_jsx(ExpandableTable, { idColumn: "applicationId", tableData: records, columnData: columns, renderExpandedSection: row => {
                    return (_jsx(Suspense, { fallback: _jsx(LinearProgress, {}), children: _jsx(ProofOfDeliveryExpandedRow, { columnData: columns, rowData: row, reload: reload }) }));
                } }), _jsx(TablePagination, { rowsPerPageOptions: [10, 20, 50], component: "div", count: recordCount, rowsPerPage: pageSize, page: page, onPageChange: (_, newPage) => {
                    setPage(newPage);
                }, onRowsPerPageChange: event => {
                    setRowsPerPage(parseInt(event.target.value, 10));
                    setPage(0);
                } })] }));
};
const ProofOfDeliveryExpandedRow = (props) => {
    const { rowData } = props;
    const proofOfDeliveryDetails = useResourceWithInvalidate(ProofOfDeliveryInformationResource.detail(), {
        applicationId: rowData.applicationId
    });
    const invalidateProofOfDelivery = useInvalidator(ProofOfDeliveryInformationResource.detail());
    useEffect(() => {
        invalidateProofOfDelivery({ applicationId: rowData.applicationId });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    const fetchApplications = useFetcher(ProofOfDeliveryInformationResource.detail());
    const reload = useCallback(() => fetchApplications({ applicationId: rowData.applicationId }), [fetchApplications, rowData.applicationId]);
    return (_jsx(ExpandedRow, { loadedData: proofOfDeliveryDetails, reloadData: reload, ...props }));
};
//# sourceMappingURL=ProofOfDeliverySms.js.map